import { WizardLayout } from '../../components/wizard/WizardLayout';
import { WizardStep } from '../../components/wizard/WizardStep';
import { de } from '../../lib/i18n/de';
import { useDimensionForm } from '../../components/wizard/fields/useDimensionForm';
import {
  BooleanField,
  NumberField,
  SelectField,
  TextareaField,
  type Option,
} from '../../components/wizard/fields/FormFields';
import { TopClientsEditor } from '../../components/wizard/fields/ArrayEditors';
import { ACTIVITY_KEYS } from '../../lib/store/dimensionScoreInput';
import { DimensionLiveScore } from '../../components/wizard/fields/DimensionLiveScore';

const t = de.dimensionFields.activity;

const SCALABILITY_OPTIONS: Option[] = Object.entries(t.scalabilityOptions).map(
  ([value, label]) => ({
    value,
    label,
  }),
);

export function ActivityStep() {
  const form = useDimensionForm('activity');
  const hasContent = Object.values(form.fields).some((v) => v.trim().length > 0);
  const recurring = form.get(ACTIVITY_KEYS.hasRecurringRevenue) === 'true';

  return (
    <WizardLayout>
      <WizardStep
        stepId="activity"
        canProceed={hasContent}
        lastSavedAt={form.lastSavedAt}
        saveState={form.lastSavedAt ? 'saved' : 'idle'}
        onProceed={form.markComplete}
      >
        <div className="space-y-8">
          <TextareaField
            id="activity-value"
            label={t.valuePropositionLabel}
            hint={t.valuePropositionHint}
            placeholder={t.valuePropositionPlaceholder}
            helpKey="activity.offering"
            minLength={30}
            value={form.get(ACTIVITY_KEYS.valueProposition)}
            onChange={(v) => {
              form.set(ACTIVITY_KEYS.valueProposition, v);
            }}
          />
          <TextareaField
            id="activity-customers"
            label={t.targetCustomersLabel}
            hint={t.targetCustomersHint}
            minLength={30}
            value={form.get(ACTIVITY_KEYS.targetCustomers)}
            onChange={(v) => {
              form.set(ACTIVITY_KEYS.targetCustomers, v);
            }}
          />
          <TopClientsEditor
            label={t.topClientsLabel}
            hint={t.topClientsHint}
            addLabel={t.topClientsAddLabel}
            value={form.get(ACTIVITY_KEYS.topClientsByRevenue)}
            onChange={(s) => {
              form.set(ACTIVITY_KEYS.topClientsByRevenue, s);
            }}
          />
          <BooleanField
            id="activity-recurring"
            label={t.hasRecurringRevenueLabel}
            required
            value={form.get(ACTIVITY_KEYS.hasRecurringRevenue)}
            onChange={(v) => {
              form.set(ACTIVITY_KEYS.hasRecurringRevenue, v);
            }}
          />
          {recurring && (
            <NumberField
              id="activity-recurring-share"
              label={t.recurringShareLabel}
              suffix={t.recurringShareSuffix}
              min={0}
              max={100}
              value={form.get(ACTIVITY_KEYS.recurringRevenueSharePercent)}
              onChange={(v) => {
                form.set(ACTIVITY_KEYS.recurringRevenueSharePercent, v);
              }}
            />
          )}
          <SelectField
            id="activity-scalability"
            label={t.scalabilityLabel}
            hint={t.scalabilityHint}
            options={SCALABILITY_OPTIONS}
            value={form.get(ACTIVITY_KEYS.scalability)}
            onChange={(v) => {
              form.set(ACTIVITY_KEYS.scalability, v);
            }}
          />
          <BooleanField
            id="activity-sales-process"
            label={t.hasDocumentedSalesProcessLabel}
            required
            value={form.get(ACTIVITY_KEYS.hasDocumentedSalesProcess)}
            onChange={(v) => {
              form.set(ACTIVITY_KEYS.hasDocumentedSalesProcess, v);
            }}
          />
          <TextareaField
            id="activity-notes"
            label={de.dimensionFields.common.notesLabel}
            hint={de.dimensionFields.common.notesHint}
            placeholder={de.dimensionFields.common.notesPlaceholder}
            rows={3}
            value={form.get(ACTIVITY_KEYS.notes)}
            onChange={(v) => {
              form.set(ACTIVITY_KEYS.notes, v);
            }}
          />
          <DimensionLiveScore dimension="activity" fields={form.fields} />
        </div>
      </WizardStep>
    </WizardLayout>
  );
}
